<template>
  <div id="order-container">
    <div>
      <div class="is-sticky">
        <basket-topper />
        <u-padding
          :y="false"
          :x="isAdmin ? ['desktop', 'widescreen', 'fullhd'] : false"
          class="basket-navigation-header"
        >
          <div
            class="is-spaced-apart is-relative has-padding-x-100"
            :class="{ container: !isAdmin }"
          >
            <div>
              <basket-currency-switcher>
                <template #default="{ currencyCode }">
                  <u-link :key="currencyCode" icon-right="chevron-down">
                    <strong>{{ currencyCode }}</strong>
                  </u-link>
                </template>
              </basket-currency-switcher>
            </div>
            <div class="is-absolute-center">
              <basket-stepper :basket-routes="basketRoutes" />
            </div>
            <basket-button v-if="!basketData.basketIsComplete" />
          </div>
        </u-padding>
      </div>
      <component
        :is="isAdmin ? 'div' : 'ribbon'"
        class="has-background-white has-padding-y-100"
      >
        <u-padding :x="isAdmin ? true : ['tablet']">
          <is-loading :if="basketData.currencyIsSwitching">
            <basket-notifications
              class="has-margin-x-100 has-margin-bottom-100"
            />
            <!-- Check: add new way of refreshing for swapping users -->
            <router-view
              v-if="!basketData.currencyIsSwitching"
              :key="routerViewKey"
            />
          </is-loading>
        </u-padding>
      </component>
    </div>
    <footer class="has-inset-shadow-top">
      <div class="container">
        <u-padding class="is-paddingless-y">
          <client-footer />
        </u-padding>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import hash from "object-hash";
import _ from "@/boot/lodash";
import Context from "@/mixins/context";
import { QUERY_PARAMS } from "@/data/constants";
import type { PropType } from "vue";
import type { IBasketProviderData } from "@/models/providers/baskets";
import type { IBasketRoute } from "@/models/baskets";

export default defineComponent({
  name: "BasketSteppedView",
  components: {
    "client-footer": () => import("@/skeleton/client/clientFooter.vue"),
    "basket-topper": () =>
      import("@/components/app/global/baskets/basket/basketTopper.vue"),
    "basket-notifications": () =>
      import("@/components/app/global/baskets/basket/basketNotifications.vue"),
    "basket-stepper": () =>
      import("@/components/app/global/baskets/basket/basketStepper.vue"),
    "basket-button": () =>
      import("@/components/app/global/baskets/basket/basketButton.vue"),
    "basket-currency-switcher": () =>
      import(
        "@/components/app/global/baskets/basket/basketCurrencySwitcher.vue"
      )
  },
  mixins: [Context],
  inject: {
    $basketData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    }
  },
  props: {
    basketRoutes: { type: Array as PropType<IBasketRoute[]>, required: true }
  },
  computed: {
    basketData() {
      return this.$basketData();
    },
    /**
     * @desc This key is required to help prevent caching issues when routing
     * directly between two different eg 'productViews'. Without some uniqueness
     * around the basket product id/product id, provision field values etc can be
     * incorrectly associated with the wrong product.
     */
    routerViewKey() {
      return hash({
        currency: this.basketData.currencyCode,
        ..._.pick(this.$route.query, [
          QUERY_PARAMS.BASKET_PRODUCT_ID,
          QUERY_PARAMS.PRODUCT_ID
        ])
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.basket-navigation-header {
  background-color: var(--brand-color);
  color: var(--brand-color-contrast);

  > div {
    height: 3rem;
  }
}

.is-sticky {
  top: 0;
  z-index: 40;
  position: sticky;
}

#order-container {
  display: flex;
  flex-direction: column;
  min-width: 20em;
  min-height: 100vh;
  // Footer
  & > footer {
    margin-top: auto;
    padding: 1.5rem;
    background-color: $primary;
    background-color: var(--brand-color);
  }
}
</style>
